import { type LiveJoinDto } from '@spin/shared';
import { useCallback, useEffect, useState } from 'react';
import { http } from '../api/http';
import { liveHandoff } from '../api/live';
import { ScreenHeader } from '../components/common';
import { reportError, useT } from '../i18n';
import { useSession } from '../store/session';

/** Title, rules and a camera preview before going live. */
export function LiveStart() {
  const t = useT();
  const [title, setTitle] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [camera, setCamera] = useState<MediaStream | null>(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let cancelled = false;
    navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode: 'user' }, audio: false })
      .then((s) => {
        if (cancelled) s.getTracks().forEach((track) => track.stop());
        else {
          stream = s;
          setCamera(s);
        }
      })
      .catch(() => setDenied(true));
    return () => {
      cancelled = true;
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  const preview = useCallback(
    (el: HTMLVideoElement | null) => {
      if (el && el.srcObject !== camera) {
        el.srcObject = camera;
        void el.play().catch(() => undefined);
      }
    },
    [camera],
  );

  const start = async () => {
    setBusy(true);
    try {
      const join = await http.post<LiveJoinDto>('/live', { title: title.trim() });
      // The preview camera is released so the stream can open it.
      camera?.getTracks().forEach((track) => track.stop());
      liveHandoff.set(join);
      const { back, go } = useSession.getState();
      back();
      go({ name: 'liveRoom', id: join.stream.id });
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="screen live-start">
      <ScreenHeader title={`🎥 ${t('live.start_title')}`} />
      <div className="live-preview">
        {camera ? (
          <video ref={preview} autoPlay playsInline muted />
        ) : (
          <span className="muted">{denied ? t('live.camera_needed') : t('live.connecting')}</span>
        )}
        <i className="live-badge">LIVE</i>
      </div>
      <input
        className="input"
        value={title}
        maxLength={80}
        placeholder={t('live.title_placeholder')}
        onChange={(e) => setTitle(e.target.value)}
        data-testid="live-title"
      />
      <label className="row live-rules">
        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
        <span>{t('live.rules')}</span>
      </label>
      <button
        className="btn btn-primary btn-block btn-lg"
        disabled={busy || !agreed || !title.trim()}
        onClick={() => void start()}
        data-testid="live-start"
      >
        🔴 {t('live.start')}
      </button>
    </div>
  );
}
